import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl, IntlShape } from 'react-intl';
import TagManager from 'react-gtm-module';

import {
  getIsLoading,
  getIsSubmitted,
  getError,
  getHasSubmittedSilent,
} from 'store/modules/callback-request/selectors';
import {
  actionSubmit, actionSilentSubmit, actionReset,
} from 'store/modules/callback-request/actions';

import { isValidEmail } from 'tools/utilities/validators/email';
import { isValidPhone } from 'tools/utilities/validators/phone';

import { getCallbackRequestModalDefaults, getIsAccountant } from 'store/modules/sign-up/selectors';
import { ADDITIONAL_COMMENTS_MAX_LENGTH } from 'store/modules/app/constants';
import { Button } from '../Button/Button';
import { TextInput } from '../TextInput/TextInput';
import { Modal } from '../Modal/Modal';
import { TextArea } from '../TextArea/TextArea';

interface IProps {
  intl: IntlShape;
}

export const CallbackRequestModalComponent = ({ intl }: IProps) => {
  const dispatch = useDispatch();

  const isAccountant = useSelector(getIsAccountant);
  const isLoading = useSelector(getIsLoading);
  const isSubmitted = useSelector(getIsSubmitted);
  const error = useSelector(getError);
  let defaultValues = useSelector(getCallbackRequestModalDefaults);
  const hasSubmittedSilent = useSelector(getHasSubmittedSilent);

  const [touchedNext, setTouchedNext] = useState(false);

  // If the user is signing up as the accountant then don't pre-populate their information with client details
  if (isAccountant) {
    defaultValues = undefined;
  }

  const [firstName, setFirstName] = useState(defaultValues?.firstName || '');
  const [lastName, setLastName] = useState(defaultValues?.lastName || '');
  const [email, setEmail] = useState(defaultValues?.email || '');
  const [phone, setPhone] = useState(defaultValues?.phone || '');
  const [comment, setComment] = useState('');

  const isFirstNameValid = !!firstName;
  const isLastNameValid = !!lastName;
  const isEmailValid = isValidEmail(email);
  const isPhoneValid = isValidPhone(phone);

  const isValid = isFirstNameValid && isLastNameValid && isEmailValid && isPhoneValid;

  const onSubmit = (evt: any) => {
    evt.preventDefault();
    setTouchedNext(true);

    if (!isValid) {
      return;
    }

    dispatch(actionSubmit({
      firstName,
      lastName,
      email,
      phone,
      comment,
      isAccountant,
    }));

    /**
     * Notifies GTM of the callback request
     */
    TagManager.dataLayer({
      dataLayer: {
        event: 'callbackRequested',
      },
    });
  };

  const handleOnClose = () => {
    if (isValid && !isSubmitted && !hasSubmittedSilent) {
      // Send callback details as per client requirements.
      dispatch(actionSilentSubmit({
        firstName,
        lastName,
        email,
        phone,
        isAccountant,
        comment,
      }));

      /**
       * Notifies GTM of the callback request
       */
      TagManager.dataLayer({
        dataLayer: {
          event: 'silentCallbackRequested',
        },
      });
    }

    dispatch(actionReset());
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setComment('');
    setTouchedNext(false);
  };

  return (
    <Modal
      onModalClose={handleOnClose}
      modalHeader={(
        <>
          {error && (
            <>
              <h2>{intl.formatMessage({ id: 'CALLBACK_REQUEST_FAIL_TITLE' })}</h2>
              <p>{intl.formatMessage({ id: 'CALLBACK_REQUEST_FAIL_SUBTITLE' })}</p>
            </>
          )}
          {isSubmitted && (
            <>
              <h2>{intl.formatMessage({ id: 'CALLBACK_REQUEST_SUCCESS_TITLE' })}</h2>
              <p>{intl.formatMessage({ id: 'CALLBACK_REQUEST_SUCCESS_SUBTITLE' })}</p>
            </>
          )}
          {!error && !isSubmitted && (
            <>
              <h2 className="u-textCenter">{intl.formatMessage({ id: 'CALLBACK_REQUEST_TITLE' })}</h2>
              <p>{intl.formatMessage({ id: 'CALLBACK_REQUEST_SUBTITLE' })}</p>
            </>
          )}
        </>
      )}
    >
      {!error && !isSubmitted && (
        <form onSubmit={onSubmit} autoComplete="off">
          <div className="Grid">
            <div className="Grid-cell u-width1of1">
              <TextInput
                id="callbackFirstName"
                label={intl.formatMessage({ id: 'FIRST_NAME' })}
                type="text"
                placeholder={intl.formatMessage({ id: 'ENTER_FIRST_NAME' })}
                onChange={(value: string) => setFirstName(value)}
                value={firstName}
                error={touchedNext && !isFirstNameValid && intl.formatMessage({ id: 'ERROR_FIRST_NAME' })}
              />

              <TextInput
                id="callbackLastName"
                label={intl.formatMessage({ id: 'LAST_NAME' })}
                type="text"
                placeholder={intl.formatMessage({ id: 'ENTER_LAST_NAME' })}
                onChange={(value: string) => setLastName(value)}
                value={lastName}
                error={touchedNext && !isLastNameValid && intl.formatMessage({ id: 'ERROR_LAST_NAME' })}
              />

              <TextInput
                id="callbackEmail"
                label={intl.formatMessage({ id: 'EMAIL_ADDRESS' })}
                type="text"
                placeholder={intl.formatMessage({ id: 'EMAIL_EXAMPLE' })}
                onChange={(value: string) => setEmail(value)}
                value={email}
                error={touchedNext && !isEmailValid && intl.formatMessage({ id: 'ERROR_EMAIL_ADDRESS' })}
              />

              <TextInput
                type="tel"
                id="callbackPhone"
                label={intl.formatMessage({ id: 'PREFERRED_CONTACT_NUMBER' })}
                placeholder={intl.formatMessage({ id: 'ENTER_NUMBER' })}
                onChange={(value: string) => setPhone(value)}
                value={phone}
                error={touchedNext && !isPhoneValid && intl.formatMessage({ id: 'ERROR_CONTACT_NUMBER' })}
              />

              <TextArea
                id="comments"
                label={intl.formatMessage({ id: 'ADDITIONAL_COMMENTS' })}
                placeholder=""
                onChange={setComment}
                value={comment}
                maxLength={ADDITIONAL_COMMENTS_MAX_LENGTH}
              />
            </div>
          </div>
          <div className="FlowButtonBar FlowButtonBar--callbackRequest">
            <Button
              label={intl.formatMessage({ id: 'CALL_ME_BACK' })}
              isLoading={isLoading}
              className="Button Button--primary"
            />
          </div>
        </form>
      )}
    </Modal>
  );
};

export const CallbackRequestModal = injectIntl(CallbackRequestModalComponent);
